/* Cortazu PDP: "Complete the outfit" checklist in the buy box (snippets/cz-pdp-outfit.liquid, q15).
   - Rows start unchecked (q124). While no main size is chosen (cz-pdp-nosize.js, q123) the button asks for one.
   - Rows follow the size the shopper picks above (FoxTheme variantChange), until a row's size is changed by hand;
     the fallback size is M, then the first available size.
   - The button adds this product (when its selected size is available) + the checked rows in one
     /cart/add.js call, with the bundled cart sections, then publishes cartUpdate and opens the drawer.
   - No discount logic (owner decision q15). Prices are the Liquid variant prices (presentment currency).
   - 19 Oct: "Size M · Dark Blue · Change" opens a panel under the row with the item's other colours (separate products of
     its colour group, JSON from Liquid) and its sizes. A colour swaps the row to that product (photo, name, link, price and
     the variant ids in the hidden select); picking a colour or size ticks the row. Sold-out sizes cannot be picked. */
(() => {
  if (customElements.get('cz-pdp-outfit')) return;

  const formatMoney = (cents, format) => {
    const value = Number(cents) || 0;
    const fmt = (n, decimals, thousands = ',', decimal = '.') => {
      const fixed = (n / 100).toFixed(decimals);
      const [int, frac] = fixed.split('.');
      const withSep = int.replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
      return frac ? `${withSep}${decimal}${frac}` : withSep;
    };
    return String(format || '€{{amount}}').replace(/\{\{\s*(\w+)\s*\}\}/, (match, key) => {
      switch (key) {
        case 'amount_no_decimals': return fmt(value, 0);
        case 'amount_with_comma_separator': return fmt(value, 2, '.', ',');
        case 'amount_no_decimals_with_comma_separator': return fmt(value, 0, '.', ',');
        case 'amount_with_apostrophe_separator': return fmt(value, 2, "'", '.');
        case 'amount_with_space_separator': return fmt(value, 2, ' ', ',');
        default: return fmt(value, 2);
      }
    });
  };

  class CzPdpOutfit extends HTMLElement {
    connectedCallback() {
      this.rows = Array.from(this.querySelectorAll('[data-cz-outfit-row]'));
      this.button = this.querySelector('[data-cz-outfit-add]');
      this.label = this.querySelector('[data-cz-outfit-label]');
      this.error = this.querySelector('[data-cz-outfit-error]');
      this.main = {
        id: this.dataset.mainVariant,
        available: this.dataset.mainAvailable === 'true',
        price: Number(this.dataset.mainPrice) || 0,
        size: this.dataset.mainSize || ''
      };
      // q123/q124: with no size preselected the main product only joins once a size is chosen.
      this.needSize = !!document.querySelector('form.cz-need-size');
      document.addEventListener('cz:size-chosen', () => { this.needSize = false; this.renderTotal(); });
      document.addEventListener('cz:size-needed', () => { this.needSize = true; this.main.size = ''; this.applySize(); });
      if (this.needSize) this.main.size = '';

      this.addEventListener('change', (event) => {
        const select = event.target.closest('[data-cz-outfit-select]');
        if (select) {
          select.closest('[data-cz-outfit-row]').dataset.touched = 'true';
          this.renderRow(select.closest('[data-cz-outfit-row]'));
        }
        this.renderTotal();
      });
      this.button.addEventListener('click', () => this.add());
      this.rows.forEach((row) => this.initRow(row));
      this.onKey = (event) => {
        if (event.key !== 'Escape') return;
        const open = this.rows.find((row) => row.querySelector('[data-cz-outfit-panel]:not([hidden])'));
        if (open) this.togglePanel(open, false, true);
      };
      this.addEventListener('keydown', this.onKey);

      const subscribe = () => {
        if (!window.FoxTheme || !FoxTheme.pubsub) return false;
        this.unsubscribe = FoxTheme.pubsub.subscribe(FoxTheme.pubsub.PUB_SUB_EVENTS.variantChange, (event) => {
          const data = event && event.data;
          if (!data || data.sectionId !== this.dataset.sectionId || !data.variant) return;
          const v = data.variant;
          this.needSize = !!document.querySelector('form.cz-need-size');
          this.main = {
            id: String(v.id),
            available: !!v.available,
            price: Number(v.price) || 0,
            size: this.sizeOf(v)
          };
          this.applySize();
        });
        return true;
      };
      if (!subscribe()) document.addEventListener('DOMContentLoaded', subscribe, { once: true });

      this.applySize();
    }

    disconnectedCallback() {
      this.unsubscribe?.();
      this.removeEventListener('keydown', this.onKey);
    }

    /* ---------- 19 Oct: colour + size panel per row ---------- */
    initRow(row) {
      // 22 Oct: the pill is a quick-view opener (aria-controls, bound by Hyper's quick-view.js); keep it from navigating.
      row.querySelector('[data-cz-outfit-qv]')?.addEventListener('click', (event) => {
        if (customElements.get('quick-view-modal')) event.preventDefault();
      });
      const toggle = row.querySelector('[data-cz-outfit-toggle]');
      if (!toggle) return;
      try {
        const data = JSON.parse(row.querySelector('[data-cz-outfit-data]')?.textContent || 'null');
        row.czColours = data && Array.isArray(data.list) && data.list.length > 1 ? data.list : null;
        row.czCurrent = data ? String(data.current) : '';
      } catch (error) {
        row.czColours = null;
      }
      toggle.addEventListener('click', () => this.togglePanel(row));
      this.renderColours(row);
      this.renderSizes(row);
    }

    togglePanel(row, force, returnFocus) {
      const panel = row.querySelector('[data-cz-outfit-panel]');
      const toggle = row.querySelector('[data-cz-outfit-toggle]');
      if (!panel || !toggle) return;
      const open = typeof force === 'boolean' ? force : panel.hidden;
      if (open) this.rows.forEach((other) => other !== row && this.togglePanel(other, false));
      panel.hidden = !open;
      toggle.setAttribute('aria-expanded', String(open));
      if (open) {
        const first = panel.querySelector('[aria-pressed="true"]:not([disabled]), button:not([disabled])');
        first?.focus({ preventScroll: true });
      } else if (returnFocus) {
        toggle.focus({ preventScroll: true });
      }
    }

    renderColours(row) {
      const wrap = row.querySelector('[data-cz-outfit-colours]');
      if (!wrap || !row.czColours) return;
      wrap.replaceChildren(
        ...row.czColours.map((colour) => {
          const button = document.createElement('button');
          button.type = 'button';
          button.className = 'cz-outfit__sw';
          button.setAttribute('aria-pressed', String(String(colour.id) === row.czCurrent));
          button.setAttribute('aria-label', colour.colour);
          button.title = colour.colour;
          if (colour.img) {
            const img = document.createElement('img');
            img.src = colour.img;
            img.alt = '';
            img.width = 40;
            img.height = 50;
            img.loading = 'lazy';
            button.append(img);
          }
          button.addEventListener('click', () => this.pickColour(row, colour));
          return button;
        })
      );
    }

    renderSizes(row) {
      const wrap = row.querySelector('[data-cz-outfit-sizes]');
      const select = row.querySelector('[data-cz-outfit-select]');
      if (!wrap || !select) return;
      wrap.replaceChildren(
        ...Array.from(select.options).map((option) => {
          const button = document.createElement('button');
          button.type = 'button';
          button.className = 'cz-outfit__size';
          button.textContent = option.dataset.size || option.textContent;
          button.disabled = option.disabled;
          button.setAttribute('aria-pressed', String(option.selected));
          if (option.disabled) button.setAttribute('aria-label', `${button.textContent} (${this.dataset.soldOut || ''})`);
          button.addEventListener('click', () => this.pickSize(row, option.value));
          return button;
        })
      );
    }

    pickSize(row, value) {
      const select = row.querySelector('[data-cz-outfit-select]');
      if (!select) return;
      select.value = value;
      row.dataset.touched = 'true';
      this.check(row);
      this.renderRow(row);
      this.renderTotal();
      this.togglePanel(row, false, true);
    }

    pickColour(row, colour) {
      const select = row.querySelector('[data-cz-outfit-select]');
      if (!select || String(colour.id) === row.czCurrent) return;
      const before = select.selectedOptions[0]?.dataset.size || this.main.size || '';
      // Rebuild the variant list of this row from the sibling product: [id, size, price, available]
      select.replaceChildren(
        ...colour.variants.map(([id, size, price, available]) => {
          const option = document.createElement('option');
          option.value = String(id);
          option.dataset.size = size || '';
          option.dataset.price = String(price);
          option.disabled = !available;
          option.textContent = size || '';
          return option;
        })
      );
      const options = Array.from(select.options).filter((o) => !o.disabled);
      const pick = options.find((o) => o.dataset.size === before) || options.find((o) => o.dataset.size === 'M') || options[0];
      if (pick) select.value = pick.value;
      row.czCurrent = String(colour.id);
      row.dataset.touched = 'true';
      // Swap photo, name and links to the other product
      const pic = row.querySelector('.cz-outfit__pic');
      if (pic && colour.img) {
        pic.removeAttribute('srcset');
        pic.src = colour.img;
      }
      row.querySelectorAll('[data-cz-outfit-link]').forEach((link) => link.setAttribute('href', colour.url));
      const name = row.querySelector('[data-cz-outfit-name]');
      if (name) name.textContent = colour.name;
      row.querySelector('[data-cz-outfit-check]')?.setAttribute('aria-label', colour.name);
      row.querySelectorAll('[data-cz-outfit-colour]').forEach((el) => (el.textContent = colour.colour));
      row.querySelectorAll('.cz-outfit__sw').forEach((button) =>
        button.setAttribute('aria-pressed', String(button.getAttribute('aria-label') === colour.colour))
      );
      this.check(row);
      this.renderSizes(row);
      this.renderRow(row);
      this.renderTotal();
    }

    check(row) {
      const box = row.querySelector('[data-cz-outfit-check]');
      if (box && !box.checked) box.checked = true;
    }

    // The size of the main variant (size option position from Liquid; none = no size to follow).
    sizeOf(variant) {
      const index = Number(this.dataset.mainSizeIndex);
      if (!(index >= 0)) return '';
      const list = variant.options || [variant.option1, variant.option2, variant.option3];
      return String(list[index] || '');
    }

    /* 27 Oct 2026 (owner, 15.52): colour per row follows the size chosen on the page. Order: same colour family as this
       product + chosen size in stock > same colour family with >= 3 core sizes > another colour with the chosen size >
       the server pick (best core-size stock). No size chosen = the server pick. Rows changed by hand are left alone.
       Data: <script data-cz-outfit-alt> per row (snippets/cz-pdp-outfit.liquid). */
    altOf(row) {
      if (row.czAlt === undefined) {
        try { row.czAlt = JSON.parse(row.querySelector('[data-cz-outfit-alt]')?.textContent || 'null'); } catch (e) { row.czAlt = null; }
        row.czShown = row.czAlt ? String(row.czAlt.cur) : '';
      }
      return row.czAlt && Array.isArray(row.czAlt.list) && row.czAlt.list.length ? row.czAlt : null;
    }

    colourFor(row, wanted) {
      const alt = this.altOf(row);
      if (!alt) return null;
      const cur = String(alt.cur);
      const list = alt.list;
      const server = list.find((c) => String(c.id) === cur) || null;
      if (!wanted) return server;
      const up = String(wanted).toUpperCase();
      const has = (c) => c.v.some((v) => v[3] && String(v[1]).toUpperCase() === up);
      const best = (arr) => {
        if (!arr.length) return null;
        return arr.find((c) => String(c.id) === cur) || arr.slice().sort((a, b) => b.n - a.n)[0];
      };
      return best(list.filter((c) => c.m === 1 && has(c))) ||
        best(list.filter((c) => c.m === 1 && c.n >= 3)) ||
        best(list.filter(has)) ||
        server;
    }

    showColour(row, c) {
      if (!c || String(c.id) === row.czShown) return;
      const select = row.querySelector('[data-cz-outfit-select]');
      if (!select) return;
      select.replaceChildren(
        ...c.v.map(([id, size, price, available]) => {
          const option = document.createElement('option');
          option.value = String(id);
          option.dataset.size = size || '';
          option.dataset.price = String(price);
          option.disabled = !available;
          option.textContent = size || '';
          return option;
        })
      );
      row.czShown = String(c.id);
      row.dataset.productId = String(c.id);
      const pic = row.querySelector('.cz-outfit__pic');
      if (pic && c.img) { pic.removeAttribute('srcset'); pic.src = c.img; }
      row.querySelectorAll('[data-cz-outfit-link], [data-cz-outfit-qv]').forEach((link) => link.setAttribute('href', c.url));
      const name = row.querySelector('[data-cz-outfit-name]');
      if (name && c.name) name.textContent = c.name;
      if (c.name) row.querySelector('[data-cz-outfit-check]')?.setAttribute('aria-label', c.name);
      row.querySelectorAll('[data-cz-outfit-colour]').forEach((el) => (el.textContent = c.colour));
    }

    applySize() {
      const wanted = this.main.size;
      this.rows.forEach((row) => {
        if (row.dataset.touched === 'true') return;
        this.showColour(row, this.colourFor(row, this.needSize ? '' : wanted));
        const select = row.querySelector('[data-cz-outfit-select]');
        if (!select || select.options.length < 2) return;
        const options = Array.from(select.options).filter((o) => !o.disabled);
        const pick =
          options.find((o) => o.dataset.size === wanted) ||
          options.find((o) => o.dataset.size === 'M') ||
          options[0];
        if (pick) select.value = pick.value;
        this.renderRow(row);
      });
      this.renderTotal();
    }

    renderRow(row) {
      const select = row.querySelector('[data-cz-outfit-select]');
      const option = select && select.selectedOptions[0];
      if (!option) return;
      const size = row.querySelector('[data-cz-outfit-size]');
      if (size) size.textContent = option.dataset.size;
      row.querySelectorAll('.cz-outfit__size').forEach((button) =>
        button.setAttribute('aria-pressed', String(button.textContent === option.dataset.size && !button.disabled))
      );
      const price = row.querySelector('[data-cz-outfit-price]');
      if (price) price.textContent = formatMoney(option.dataset.price, this.dataset.moneyFormat);
    }

    selection() {
      const items = [];
      let total = 0;
      if (this.main.available && this.main.id && !this.needSize) {
        items.push({ id: Number(this.main.id), quantity: 1 });
        total += this.main.price;
      }
      this.rows.forEach((row) => {
        if (!row.querySelector('[data-cz-outfit-check]')?.checked) return;
        const option = row.querySelector('[data-cz-outfit-select]')?.selectedOptions[0];
        if (!option || option.disabled) return;
        items.push({ id: Number(option.value), quantity: 1 });
        total += Number(option.dataset.price) || 0;
      });
      return { items, total };
    }

    renderTotal() {
      if (this.needSize) {
        this.label.textContent = this.dataset.labelSize || '';
        this.button.disabled = false;
        return;
      }
      const { items, total } = this.selection();
      const template = items.length === 1 ? this.dataset.labelOne : this.dataset.labelMany;
      this.label.textContent = String(template || '')
        .replace('[count]', items.length)
        .replace('[total]', formatMoney(total, this.dataset.moneyFormat));
      this.button.disabled = items.length === 0;
    }

    showError(message) {
      this.error.textContent = message || '';
      this.error.hidden = !message;
    }

    openDrawer() {
      const drawer = document.querySelector('cart-drawer');
      if (!drawer) return false;
      if (typeof drawer.show === 'function') drawer.show();
      else if (typeof drawer.open === 'function') drawer.open();
      else return false;
      return true;
    }

    async add() {
      if (this.needSize) {
        // Hand over to the main button: cz-pdp-nosize.js shows the hint and scrolls to the size picker.
        document.querySelector('form.cz-need-size [name="add"]')?.click();
        return;
      }
      const { items } = this.selection();
      if (!items.length || this.button.getAttribute('aria-busy') === 'true') return;
      this.button.setAttribute('aria-busy', 'true');
      this.button.classList.add('is-loading');
      this.showError('');

      const sections = [];
      document.documentElement.dispatchEvent(
        new CustomEvent('cart:grouped-sections', { bubbles: true, detail: { sections } })
      );

      try {
        const addUrl = (window.FoxTheme && FoxTheme.routes && FoxTheme.routes.cart_add_url) || '/cart/add';
        const config = window.FoxTheme?.utils?.fetchConfig
          ? FoxTheme.utils.fetchConfig('javascript')
          : { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/javascript' } };
        const response = await fetch(`${addUrl}.js`, {
          ...config,
          body: JSON.stringify({ items, sections, sections_url: window.location.pathname })
        });
        const parsed = await response.json();
        if (parsed.status) throw new Error(parsed.description || parsed.message || `HTTP ${parsed.status}`);

        const cartUrl = (window.FoxTheme && FoxTheme.routes && FoxTheme.routes.cart_url) || '/cart';
        const cartJson = await (await fetch(`${cartUrl}.js`, { headers: { Accept: 'application/json' } })).json();
        cartJson.sections = parsed.sections;
        if (window.FoxTheme?.pubsub) {
          FoxTheme.pubsub.publish(FoxTheme.pubsub.PUB_SUB_EVENTS.cartUpdate, { cart: cartJson });
        }
        const first = Array.isArray(parsed.items) ? parsed.items[0] : parsed;
        document.dispatchEvent(new CustomEvent('product-ajax:added', { detail: { product: first } }));
        if (!this.openDrawer()) window.location.href = cartUrl;
      } catch (error) {
        console.error('[cz-pdp-outfit]', error);
        this.showError(this.dataset.error);
      } finally {
        this.button.removeAttribute('aria-busy');
        this.button.classList.remove('is-loading');
      }
    }
  }

  customElements.define('cz-pdp-outfit', CzPdpOutfit);
})();
